import { Icon } from '@sapphire-react/core';
import { Wallet } from '@sapphire/icons/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import type { Control, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSapphireMedia } from '../../hooks';
import { AmountInputField } from '../AmountInputField';

type Props = {
  control: Control<any>;
  fieldFormValue?: string;
};

export function PrimaryApplicantIncome({ control, fieldFormValue }: Props) {
  const [primaryApplicantIncomeNote, setPrimaryApplicantIncomeNote] =
    useState<string>();
  const [isPrimaryApplicantIncomeDirty, setIsPrimaryApplicantIncomeDirty] =
    useState<boolean>();
  const { t } = useTranslation();
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const primaryApplicantIncomeRules: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  > = {
    required: t(
      'shared:personalInformation.inputs.monthlyIncome.requiredError',
    ),
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDebouncedPrimaryApplicantIncomeNote = useCallback(
    debounce(
      (value: string) => {
        setPrimaryApplicantIncomeNote(getPrimaryApplicantIncomeNote(value));
        !isPrimaryApplicantIncomeDirty &&
          setIsPrimaryApplicantIncomeDirty(true);
      },
      isPrimaryApplicantIncomeDirty ? 0 : 4000,
    ),
    [isPrimaryApplicantIncomeDirty],
  );

  const getPrimaryApplicantIncomeNote = (income?: string) => {
    if (income && (parseInt(income) > 199999 || parseInt(income) < 8000)) {
      return 'Är du säker på att detta är din månatliga inkomst?';
    }

    return undefined;
  };

  return (
    <AmountInputField
      control={control}
      name={'primaryApplicantIncome'}
      label={t('shared:personalInformation.inputs.monthlyIncome.label')}
      rules={primaryApplicantIncomeRules}
      width={'100%'}
      postfix={'kr'}
      min={0}
      max={100_000_000}
      onBlur={() => setDebouncedPrimaryApplicantIncomeNote('')}
      note={primaryApplicantIncomeNote}
      prefix={
        <Icon size={inputSize}>
          <Wallet />
        </Icon>
      }
      onFocus={() => {
        setPrimaryApplicantIncomeNote(
          getPrimaryApplicantIncomeNote(fieldFormValue),
        );
      }}
      onChange={(value) =>
        value && setDebouncedPrimaryApplicantIncomeNote(value)
      }
    />
  );
}
